/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	UserPreferenceCategories,
} from '@touchplan/fancy-referencer';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';

import firebase from 'firebase/compat/app';

export interface IHoverOverUserPreferences {
	isEnabled: boolean;
	displayDescription: boolean;
	displayRole: boolean;
	displayLocation: boolean;
	displayDuration: boolean;
	displayCrew: boolean;
	displayAssignTo: boolean;
	displayNeedByDate: boolean;
	displayImageAttachmentsCount: boolean;
	displayAllCustomFields: boolean;
}

export const hoverOverUserPreferencesDefaults: IHoverOverUserPreferences = {
	isEnabled: true,
	displayDescription: true,
	displayRole: true,
	displayLocation: true,
	displayDuration: true,
	displayCrew: true,
	displayAssignTo: true,
	displayNeedByDate: true,
	displayImageAttachmentsCount: true,
	displayAllCustomFields: false,
};

@Injectable({
	providedIn: 'root',
})
export class HoverOverUserPreferencesService implements OnDestroy {
	private shutdownSubject$: Subject<void> = new Subject();

	constructor(private fireDB: AngularFireDatabase, private featureFlagManager: FeatureFlagManager) {}

	/**
	 * This method updates the hover-over user preferences for a given userId and projectId
	 * @param userId - a string with ID of user
	 * @param projectId - a string with ID of project
	 * @param hoverOverUserPreferences - user project hover-over preferences to update
	 */
	public updateHoverOverUserProjectPreferences(
		userId: string,
		projectId: string,
		hoverOverUserPreferences: Partial<IHoverOverUserPreferences>
	): void {
		this.getHoverOverUserProjectPreferencesFireObject(userId, projectId).update(hoverOverUserPreferences);
	}

	/**
	 * This method returns a observable that emits hover-over user project preferences changes for given userId and projectId
	 * @param userId - a string with ID of user
	 * @param projectId - a string with ID of project
	 * @returns - An observable of hover-over user preferences
	 */
	public hoverOverUserProjectPreferencesChanges$(
		userId: string,
		projectId: string
	): Observable<Partial<IHoverOverUserPreferences>> {
		return this.getHoverOverUserProjectPreferencesFireObject(userId, projectId)
			.valueChanges()
			.pipe(
				takeUntil(this.shutdownSubject$),
				map((settings) => this.getDefaultSettingsForHoverOver(settings))
			);
	}

	/**
	 * this method returns an observable that emit true when hover over preference is enabled for a given project
	 * @param userId - a string with ID of user
	 * @param projectId - a string with ID of project
	 * @returns an observable that emits true when hover-over preference is enabled
	 */
	public isHoverOverEnabled$(userId: string, projectId: string): Observable<boolean> {
		return combineLatest([
			this.hoverOverUserProjectPreferencesChanges$(userId, projectId).pipe(
				map((preferences) => !!preferences?.isEnabled)
			),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.PLAN_ACTIONS_HOVER_OVER),
		]).pipe(
			map(([isHoverOverPreferenceEnabled, isHoverOverFeatureFlagEnabled]) =>
				isHoverOverFeatureFlagEnabled ? isHoverOverPreferenceEnabled : false
			),
			distinctUntilChanged(),
			takeUntil(this.shutdownSubject$)
		);
	}

	/**
	 * this method set the enabled custom fields for hover-over for a given userId and projectId
	 * this will override any existing enabled custom fields
	 * @param userId - a string with ID of user
	 * @param projectId - a string with ID of project
	 * @param enabledCustomFields - enabled custom fields IDs
	 */
	public setHoverOverUserProjectEnabledCustomFields(
		userId: string,
		projectId: string,
		enabledCustomFields: string[]
	) {
		const enabledCustomFieldsDictionary = enabledCustomFields.reduce(
			(outputDictionary, customFieldId) => ({ ...outputDictionary, [customFieldId]: customFieldId }),
			{}
		);
		this.getHoverOverUserProjectCustomFieldsFireObject(userId, projectId).set(enabledCustomFieldsDictionary);
	}

	/**
	 * this method emits changes on enabled hover-over custom fields for a given userId and projectId
	 * @param userId - a string with ID of user
	 * @param projectId - a string with ID of project
	 * @returns - An observable with enabled custom fields IDs
	 */
	public hoverOverUserProjectEnabledCustomFieldsChanges$(
		userId: string,
		projectId: string
	): Observable<string[]> {
		return this.getHoverOverUserProjectCustomFieldsFireObject(userId, projectId)
			.valueChanges()
			.pipe(
				map((enabledCustomFieldsDictionary) =>
					enabledCustomFieldsDictionary ? Object.keys(enabledCustomFieldsDictionary) : []
				),
				takeUntil(this.shutdownSubject$)
			);
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	private getHoverOverUserProjectPreferencesFireObject(
		userId: string,
		projectId: string
	): AngularFireObject<Partial<IHoverOverUserPreferences>> {
		const path = fancyFirebaseGetters.getProjectUserPreferencesPath(
			projectId,
			userId,
			UserPreferenceCategories.HoverPrefs
		);
		return this.fireDB.object<Partial<IHoverOverUserPreferences>>(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId)
		);
	}

	private getHoverOverUserProjectCustomFieldsFireObject(
		userId: string,
		projectId: string
	): AngularFireObject<{ [key: string]: string }> {
		const path = fancyFirebaseGetters.getProjectUserPreferencesPath(
			projectId,
			userId,
			UserPreferenceCategories.HoverPrefsCustomFields
		);
		return this.fireDB.object<{ [key: string]: string }>(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId)
		);
	}

	// Returns the default values for the HoverOverUserPreferences. Optionally receives a partial settings object to replace default values.
	// We might have to change the spread operator for a deep cloning method in the future if the object starts having more than 1 level deep.
	private getDefaultSettingsForHoverOver(
		settings?: Partial<IHoverOverUserPreferences>
	): Partial<IHoverOverUserPreferences> {
		return {
			...hoverOverUserPreferencesDefaults,
			...settings,
		};
	}
}
